import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiBell, FiSend, FiBarChart2, FiEye, FiMousePointer, FiX, FiTrash2 } = FiIcons;

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ title: '', body: '', action_url: '' });
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    // Fetch notifications
    const { data: notifs, error } = await supabase
      .from('app_notifications')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && notifs) {
      setNotifications(notifs);
      
      // Fetch stats for these notifications
      const { data: reads } = await supabase
        .from('app_notification_reads')
        .select('notification_id, viewed_at, clicked_at, dismissed_at');
        
      const newStats = {};
      notifs.forEach(n => {
        const nReads = reads?.filter(r => r.notification_id === n.id) || [];
        newStats[n.id] = {
          views: nReads.filter(r => r.viewed_at).length,
          clicks: nReads.filter(r => r.clicked_at).length,
          dismissals: nReads.filter(r => r.dismissed_at).length
        };
      });
      setStats(newStats);
    }
    setLoading(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.body) return;

    const { error } = await supabase.from('app_notifications').insert({
      title: formData.title,
      body: formData.body,
      action_url: formData.action_url || null
    });

    if (!error) {
      setFormData({ title: '', body: '', action_url: '' });
      fetchNotifications();
      alert('Notificação enviada com sucesso!');
    } else {
      alert('Erro ao enviar notificação');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja apagar esta notificação?')) {
      await supabase.from('app_notifications').delete().eq('id', id);
      fetchNotifications();
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row gap-8">
        {/* Create Notification Form */}
        <div className="w-full md:w-1/3 space-y-6">
          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                <SafeIcon icon={FiSend} className="text-xl" />
              </div>
              <div>
                <h3 className="font-black text-slate-900 uppercase italic">Nova Notificação</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Enviar para todos os usuários</p>
              </div>
            </div>

            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-2">Título</label>
                <input 
                  type="text" 
                  value={formData.title}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm font-bold text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="Ex: Promoção de Créditos"
                  required
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-2">Mensagem</label>
                <textarea 
                  value={formData.body}
                  onChange={e => setFormData({...formData, body: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none min-h-[100px]"
                  placeholder="Digite o conteúdo da notificação..."
                  required
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-2">URL de Ação (Opcional)</label>
                <input 
                  type="url" 
                  value={formData.action_url}
                  onChange={e => setFormData({...formData, action_url: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm font-bold text-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="https://..."
                />
              </div>

              <button 
                type="submit" 
                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-xl uppercase text-xs tracking-widest transition-all shadow-lg shadow-indigo-200 mt-2 flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiSend} /> Enviar Agora
              </button>
            </form>
          </div>
        </div>

        {/* List & Stats */}
        <div className="w-full md:w-2/3">
          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-8 border-b border-slate-100">
              <h3 className="font-black text-slate-900 uppercase italic">Histórico e Estatísticas</h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Acompanhe o engajamento</p>
            </div>

            <div className="divide-y divide-slate-50">
              {loading ? (
                <div className="p-12 text-center text-slate-400 text-xs font-bold uppercase tracking-widest">Carregando...</div>
              ) : notifications.length === 0 ? (
                <div className="p-12 text-center text-slate-400 text-xs font-bold uppercase tracking-widest">Nenhuma notificação enviada.</div>
              ) : notifications.map(notif => (
                <div key={notif.id} className="p-6 hover:bg-slate-50/50 transition-colors group">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-bold text-slate-800">{notif.title}</h4>
                      <p className="text-sm text-slate-500 mt-1 max-w-xl">{notif.body}</p>
                      {notif.action_url && (
                        <a href={notif.action_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-500 font-bold mt-2 inline-block hover:underline">
                          {notif.action_url}
                        </a>
                      )}
                    </div>
                    <button 
                      onClick={() => handleDelete(notif.id)}
                      className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-bold">
                      <SafeIcon icon={FiEye} />
                      {stats[notif.id]?.views || 0} Visualizações
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-600 rounded-lg text-xs font-bold">
                      <SafeIcon icon={FiMousePointer} />
                      {stats[notif.id]?.clicks || 0} Cliques
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-500 rounded-lg text-xs font-bold">
                      <SafeIcon icon={FiX} />
                      {stats[notif.id]?.dismissals || 0} Fechados
                    </div>
                  </div>
                  
                  <div className="mt-2 text-[10px] text-slate-300 font-bold uppercase tracking-wider">
                    Enviado em {new Date(notif.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}